import React, { useState, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import style from "./style.module.css";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";

const PAGE_SIZE = 25;

interface AddressRecord {
  address_id: number;
  user_id: number;
  address_1: string;
  address_2: string;
  address_name: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  // Embedded directly by GET /address/addresses (not fetched separately via
  // GET /user/users) — that endpoint is O/A-only and 403s for Sales, who is
  // a legitimate caller of this page (canReadOwn permits O/A/S/C; Staff
  // O/A/S can view any address per addressRouter.js's own comment).
  user_first_name: string | null;
  user_last_name: string | null;
  created_at: string;
  updated_at: string;
}

const AddressPage: React.FC = () => {
  const [addresses, setAddresses] = useState<AddressRecord[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [cookies] = useCookies();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchAddresses = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearch) params.set("search", debouncedSearch);
      const response = await authenticatedFetch(`${API_BASE}/address/addresses?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setAddresses(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch addresses. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  }, [cookies.auth, page, debouncedSearch]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const filtered = addresses;

  // View-only page: no selection or CRUD

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Address</h1>
        <div className={style.searchSection}>
            <input
              type="text"
              placeholder="Search addresses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={style.searchInput}
            />
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.table}>
          <thead>
            <tr>
              <th>Address ID</th>
              <th>User</th>
              <th>Address Name</th>
              <th>Address 1</th>
              <th>Address 2 </th>
              <th>City</th>
              <th>Country</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                  {addresses.length === 0 ? "No addresses found. Loading..." : "No addresses match your search criteria."}
                </td>
              </tr>
            ) : (
              filtered.map(address => (
                <tr key={address.address_id}>
                  <td>{address.address_id}</td>
                  <td>{address.user_first_name} {address.user_last_name}</td>
                  <td>{address.address_name}</td>
                  <td>{address.address_1}</td>
                  <td>{address.address_2}</td>
                  <td>{address.city}</td>
                  <td>{address.country}</td>
                  <td>{address.latitude}</td>
                  <td>{address.longitude}</td>
                  <td>{new Date(address.created_at).toLocaleDateString()}</td>
                  <td>{new Date(address.updated_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />
    </div>
  );
};

export default AddressPage;

